/// <reference types="vite/client" />
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Activity,
  AlertTriangle,
  Database,
  Eye,
  Globe2,
  HardDrive,
  RefreshCw,
  ShieldCheck,
  Users,
  Zap,
} from "lucide-react";
import MetricCard from "../components/MetricCard";
import {
  type CfSummary,
  type CfTimeseriesPoint,
  type CfTopRow,
  type CfWindow,
  type CfZone,
  fetchSummary,
  fetchTimeseries,
  fetchTop,
  formatBytes,
  listZones,
} from "../lib/cfMetrics";

const REFRESH_MS = 60_000;

const WINDOWS: Array<{ key: CfWindow; label: string; interval: string }> = [
  { key: "1h", label: "1H", interval: "1m" },
  { key: "24h", label: "24H", interval: "1h" },
  { key: "7d", label: "7D", interval: "1h" },
  { key: "30d", label: "30D", interval: "1d" },
];

interface LoadState {
  loading: boolean;
  error: string | null;
}

export default function SiteMetrics() {
  const [zones, setZones] = useState<CfZone[] | null>(null);
  const [zoneId, setZoneId] = useState<string | null>(null);
  const [windowKey, setWindowKey] = useState<CfWindow>("24h");

  const [summary, setSummary] = useState<CfSummary | null>(null);
  const [series, setSeries] = useState<CfTimeseriesPoint[] | null>(null);
  const [topCountries, setTopCountries] = useState<CfTopRow[] | null>(null);
  const [topStatuses, setTopStatuses] = useState<CfTopRow[] | null>(null);
  const [topHosts, setTopHosts] = useState<CfTopRow[] | null>(null);

  const [{ loading, error }, setLoad] = useState<LoadState>({
    loading: true,
    error: null,
  });
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Load zones once.
  useEffect(() => {
    const ctrl = new AbortController();
    listZones(ctrl.signal)
      .then((zs) => {
        setZones(zs);
        const preferred =
          zs.find((z) => z.name === "willbracken.com") ?? zs[0] ?? null;
        if (preferred) setZoneId(preferred.id);
        if (!preferred) setLoad({ loading: false, error: "no zones available" });
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : "failed to load zones";
        setLoad({ loading: false, error: msg });
      });
    return () => ctrl.abort();
  }, []);

  const interval = useMemo(
    () => WINDOWS.find((w) => w.key === windowKey)?.interval ?? "1h",
    [windowKey],
  );

  const load = useCallback(async () => {
    if (!zoneId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoad({ loading: true, error: null });
    try {
      const [s, ts, c, st, h] = await Promise.all([
        fetchSummary(zoneId, windowKey, ctrl.signal),
        fetchTimeseries(zoneId, windowKey, interval, ctrl.signal),
        fetchTop(zoneId, windowKey, "country", 10, ctrl.signal),
        fetchTop(zoneId, windowKey, "status", 10, ctrl.signal),
        fetchTop(zoneId, windowKey, "host", 10, ctrl.signal),
      ]);
      setSummary(s);
      setSeries(ts);
      setTopCountries(c);
      setTopStatuses(st);
      setTopHosts(h);
      setRefreshedAt(Date.now());
      setLoad({ loading: false, error: null });
    } catch (e: unknown) {
      if ((e as { name?: string }).name === "AbortError") return;
      const msg = e instanceof Error ? e.message : "failed to load metrics";
      setLoad({ loading: false, error: msg });
    }
  }, [zoneId, windowKey, interval]);

  useEffect(() => {
    if (!zoneId) return;
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(t);
  }, [zoneId, load]);

  const bandwidth = summary ? formatBytes(summary.bandwidth_bytes) : null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="eyebrow">Layer · Edge Analytics</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight gradient-text">
            Site Metrics
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Live request, bandwidth, and threat data sourced from the Cloudflare
            GraphQL Analytics API.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {refreshedAt && (
            <span className="font-mono">
              updated {new Date(refreshedAt).toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={() => void load()}
            className="glass glass-hover px-2.5 py-1.5 rounded-md flex items-center gap-1.5 text-slate-300"
            aria-label="Refresh metrics"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Selectors */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="eyebrow">Zone</span>
          <select
            value={zoneId ?? ""}
            onChange={(e) => setZoneId(e.target.value)}
            disabled={!zones}
            className="glass px-3 py-1.5 rounded-md bg-transparent text-sm text-slate-200 border border-white/[0.08] focus:outline-none focus:border-accent-blue/40"
          >
            {!zones && <option>loading…</option>}
            {zones?.map((z) => (
              <option key={z.id} value={z.id} className="bg-slate-900">
                {z.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1 glass rounded-md p-1">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              onClick={() => setWindowKey(w.key)}
              className={`px-3 py-1 rounded text-xs font-mono transition-colors ${
                windowKey === w.key
                  ? "bg-accent-blue/15 text-accent-blue"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-2 text-xs text-accent-rose">
            <AlertTriangle size={12} />
            <span className="font-mono">{error}</span>
          </div>
        )}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {summary ? (
          <>
            <MetricCard
              label="Requests"
              value={summary.requests}
              icon={Activity}
              accent="text-accent-blue"
            />
            <MetricCard
              label="Bandwidth"
              value={bandwidth?.value ?? 0}
              unit={bandwidth?.unit ?? "B"}
              icon={HardDrive}
              accent="text-accent-emerald"
              decimals={1}
            />
            <MetricCard
              label="Threats Blocked"
              value={summary.threats}
              icon={ShieldCheck}
              accent="text-accent-rose"
            />
            <MetricCard
              label="Cache Hit"
              value={summary.cache_hit_ratio * 100}
              icon={Zap}
              accent="text-accent-amber"
              format="percent"
              decimals={1}
            />
            <MetricCard
              label="Unique Visitors"
              value={summary.unique_visitors}
              icon={Users}
              accent="text-accent-blue"
            />
            <MetricCard
              label="Page Views"
              value={summary.page_views}
              icon={Eye}
              accent="text-accent-emerald"
            />
          </>
        ) : (
          Array.from({ length: 6 }).map((_, i) => <KpiSkeleton key={i} />)
        )}
      </div>

      {/* Time series chart */}
      <div className="glass ring-glass p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow">Traffic</div>
            <div className="text-sm text-slate-300 mt-0.5">
              Requests &amp; bandwidth over the selected window
            </div>
          </div>
          <div className="flex items-center gap-3 text-[11px] font-mono text-slate-400">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-accent-blue" />
              requests
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-accent-emerald" />
              bandwidth
            </span>
          </div>
        </div>
        <div className="h-64">
          {series && series.length > 0 ? (
            <DualAreaChart points={series} />
          ) : (
            <div className="shimmer h-full rounded-md" />
          )}
        </div>
      </div>

      {/* Top X lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TopList
          title="Top Countries"
          icon={Globe2}
          rows={topCountries}
          accent="text-accent-blue"
        />
        <TopList
          title="Top Status Codes"
          icon={Database}
          rows={topStatuses}
          accent="text-accent-amber"
        />
        <TopList
          title="Top Hosts"
          icon={HardDrive}
          rows={topHosts}
          accent="text-accent-emerald"
        />
      </div>
    </div>
  );
}

/* ---------- Top list ---------- */

function TopList({
  title,
  icon: Icon,
  rows,
  accent,
}: {
  title: string;
  icon: typeof Activity;
  rows: CfTopRow[] | null;
  accent: string;
}) {
  const max = rows && rows.length ? Math.max(...rows.map((r) => r.requests)) : 1;
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="glass ring-glass p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="eyebrow">{title}</div>
        <Icon size={14} className={accent} strokeWidth={1.75} />
      </div>
      {!rows ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="shimmer h-6 rounded" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-500 font-mono">no data</div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const pct = max > 0 ? (r.requests / max) * 100 : 0;
            return (
              <li key={r.key} className="relative">
                <div className="flex items-center justify-between text-xs font-mono mb-0.5">
                  <span className="text-slate-300 truncate pr-2">{r.key}</span>
                  <span className="text-slate-400">
                    {r.requests.toLocaleString()}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-white/[0.04] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-accent-blue/60 to-accent-blue/20"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </motion.div>
  );
}

/* ---------- Inline-SVG dual area chart ---------- */

function DualAreaChart({ points }: { points: CfTimeseriesPoint[] }) {
  const W = 800;
  const H = 220;
  const PAD_X = 36;
  const PAD_Y = 16;

  const reqs = points.map((p) => p.requests);
  const band = points.map((p) => p.bandwidth);
  const maxR = Math.max(1, ...reqs);
  const maxB = Math.max(1, ...band);
  const n = points.length;

  const xAt = (i: number) =>
    n === 1 ? W / 2 : PAD_X + (i / (n - 1)) * (W - PAD_X * 2);
  const yReq = (v: number) =>
    H - PAD_Y - (v / maxR) * (H - PAD_Y * 2);
  const yBand = (v: number) =>
    H - PAD_Y - (v / maxB) * (H - PAD_Y * 2);

  const reqPath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${xAt(i).toFixed(2)},${yReq(p.requests).toFixed(2)}`)
    .join(" ");
  const bandPath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${xAt(i).toFixed(2)},${yBand(p.bandwidth).toFixed(2)}`)
    .join(" ");

  const reqArea =
    n > 0
      ? `${reqPath} L${xAt(n - 1).toFixed(2)},${H - PAD_Y} L${xAt(0).toFixed(2)},${H - PAD_Y} Z`
      : "";
  const bandArea =
    n > 0
      ? `${bandPath} L${xAt(n - 1).toFixed(2)},${H - PAD_Y} L${xAt(0).toFixed(2)},${H - PAD_Y} Z`
      : "";

  // Y-axis tick labels (left = requests, right = bandwidth)
  const ticks = 4;
  const reqTicks = Array.from({ length: ticks + 1 }, (_, i) => {
    const v = (maxR * (ticks - i)) / ticks;
    return { y: PAD_Y + (i / ticks) * (H - PAD_Y * 2), label: shortNum(v) };
  });
  const bandTicks = Array.from({ length: ticks + 1 }, (_, i) => {
    const v = (maxB * (ticks - i)) / ticks;
    const fb = formatBytes(v);
    return {
      y: PAD_Y + (i / ticks) * (H - PAD_Y * 2),
      label: `${fb.value}${fb.unit}`,
    };
  });

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      role="img"
      aria-label="Requests and bandwidth over time"
    >
      <defs>
        <linearGradient id="cfm-req" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.35} />
          <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
        </linearGradient>
        <linearGradient id="cfm-band" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34d399" stopOpacity={0.3} />
          <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
        </linearGradient>
      </defs>

      {/* horizontal grid */}
      {reqTicks.map((t, i) => (
        <line
          key={`g-${i}`}
          x1={PAD_X}
          x2={W - PAD_X}
          y1={t.y}
          y2={t.y}
          stroke="rgba(255,255,255,0.04)"
          strokeWidth={1}
        />
      ))}

      {/* left axis labels — requests */}
      {reqTicks.map((t, i) => (
        <text
          key={`lr-${i}`}
          x={PAD_X - 4}
          y={t.y + 3}
          textAnchor="end"
          fontSize="9"
          fill="#60a5fa"
          fontFamily="monospace"
        >
          {t.label}
        </text>
      ))}

      {/* right axis labels — bandwidth */}
      {bandTicks.map((t, i) => (
        <text
          key={`rb-${i}`}
          x={W - PAD_X + 4}
          y={t.y + 3}
          textAnchor="start"
          fontSize="9"
          fill="#34d399"
          fontFamily="monospace"
        >
          {t.label}
        </text>
      ))}

      {bandArea && <path d={bandArea} fill="url(#cfm-band)" />}
      {bandPath && (
        <path
          d={bandPath}
          fill="none"
          stroke="#34d399"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {reqArea && <path d={reqArea} fill="url(#cfm-req)" />}
      {reqPath && (
        <path
          d={reqPath}
          fill="none"
          stroke="#60a5fa"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

function shortNum(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return Math.round(v).toString();
}

/* ---------- skeletons ---------- */

function KpiSkeleton() {
  return (
    <div className="glass ring-glass p-5">
      <div className="shimmer h-3 w-20 rounded" />
      <div className="shimmer h-8 w-32 rounded mt-3" />
      <div className="shimmer h-3 w-16 rounded mt-3" />
    </div>
  );
}
